import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import * as R from 'ramda'

import useConfirmAlert from '~/components/common/dialog/useConfirmAlert'
import { getTotal } from '~/components/dashboard/invoices/invoiceUtils'
import { ConfirmAlertType } from '~/constants/DialogNames'
import { removeLineItems } from '~/store/actions/finance'
import type { Estimate, InvoiceLineItem, Order } from '~/types'

interface UseEstimateUncheckItemProps {
  estimate: Estimate
  updateInvoice: (invoice: Estimate) => void
}

export const useEstimateUncheckItem = ({
  estimate,
  updateInvoice,
}: UseEstimateUncheckItemProps) => {
  const dispatch = useDispatch()
  const { t } = useTranslation('Common')
  const [openConfirmAlert] = useConfirmAlert({
    type: ConfirmAlertType.INVOICE_TABLE_ITEM,
  })

  const getIsGroupedItem = useCallback(
    (item: InvoiceLineItem) => {
      const groups = estimate?.groups || []
      const group = groups[0]
      return group.groupedItems.some((groupedItem) =>
        groupedItem.items?.includes(item),
      )
    },
    [estimate],
  )

  const findMatchingLineItem = useCallback(
    (order: Order, isBundle: boolean): InvoiceLineItem | undefined => {
      const groups = estimate?.groups || []
      const group = groups[0] // Estimates have single group
      const items = group?.groupedItems || []

      // If checking a bundle, find the bundle parent by matching bundle items
      if (isBundle && order.items) {
        return items.find((lineItem) => {
          if (!lineItem.items) return false
          // Match bundle by checking if all bundle items match
          return order.items!.every((orderItem) =>
            lineItem.items!.some(
              (bundleItem) =>
                bundleItem.logType === orderItem.type &&
                bundleItem.name === orderItem.name,
            ),
          )
        })
      }

      // For single items, find the item (could be in a bundle or standalone)
      // First, search for nested items within bundles
      const foundInBundle = items
        .filter((lineItem) => !R.isNil(lineItem.items) && lineItem.items)
        .flatMap((lineItem) => lineItem.items!)
        .find(
          (nestedItem) =>
            nestedItem.logType === order.type && nestedItem.name === order.name,
        )

      if (foundInBundle) return foundInBundle

      // Then search for standalone items
      return items.find(
        (lineItem) =>
          lineItem.logType === order.type && lineItem.name === order.name,
      )
    },
    [estimate],
  )

  const deleteItem = useCallback(
    (item: InvoiceLineItem) => {
      const groups = estimate?.groups || []
      const group = groups[0]

      const isGroupedItem = getIsGroupedItem(item)
      const isGroup = Boolean(item.items)

      const itemParent: InvoiceLineItem | null =
        isGroupedItem && !isGroup
          ? (group.groupedItems.find((groupItem) =>
              groupItem.items?.includes(item),
            ) as InvoiceLineItem)
          : null

      const itemIndex = itemParent ? group.groupedItems.indexOf(itemParent) : -1
      const groupIndex = groups.indexOf(group)

      const newParentItem: InvoiceLineItem | null =
        isGroupedItem && !isGroup
          ? (() => {
              const updatedParent = {
                ...(itemParent as InvoiceLineItem),
                items: R.without([item], itemParent?.items || []),
              }
              // Recalculate parent bundle's subTotal after removing child
              if (updatedParent.items && updatedParent.items.length > 0) {
                updatedParent.subTotal = getTotal(
                  updatedParent,
                  estimate,
                ) as number
              }
              return updatedParent
            })()
          : null

      // Determine updated groupedItems after deletion
      const getUpdatedGroupedItems = () => {
        // If parent bundle is now empty, remove the entire bundle
        if (newParentItem && !newParentItem.items?.length) {
          return R.without([itemParent], group.groupedItems)
        }
        // If parent bundle still has items, update it
        if (newParentItem) {
          return R.update(itemIndex, newParentItem, group.groupedItems)
        }
        // Otherwise, remove the individual item
        return R.without([item], group.groupedItems)
      }

      const newGroup = {
        ...group,
        groupedItems: getUpdatedGroupedItems(),
      }

      const newGroups =
        newGroup.groupedItems.length === 0
          ? R.without([group], groups)
          : R.update(groupIndex, newGroup, groups)

      const bundleAdditionalDiscount =
        (estimate.bundleAdditionalDiscount || 0) -
        (item.additionalDiscount || 0)

      updateInvoice({
        ...estimate,
        groups: newGroups,
        bundleAdditionalDiscount,
      })

      // Only dispatch Redux action if items have IDs
      if (estimate.id) {
        const idsToRemove = R.pluck('id', item.items || [item]).filter(Boolean)
        if (idsToRemove.length > 0) {
          dispatch(removeLineItems(idsToRemove, estimate.id))
        }
      }
    },
    [estimate, getIsGroupedItem, updateInvoice, dispatch],
  )

  return useCallback(
    (order: Order, isBundle = false) => {
      const proceedToRemoveOrder = () => {
        const matchingItem = findMatchingLineItem(order, isBundle)

        if (matchingItem) {
          deleteItem(matchingItem)
        }
      }

      openConfirmAlert({
        message: t('Common:YOU_WILL_REMOVE_SOMETHING', {
          something: order.name,
        }),
        onConfirm: (proceed) => proceed && proceedToRemoveOrder(),
      })
    },
    [findMatchingLineItem, deleteItem, openConfirmAlert, t],
  )
}
